import { fetcher } from "@/lib/api/fetcher";
import { environments } from "@/lib/config";
import { InquiryRequest, InquiryData } from "@/types/service-inquiry";
import { ResponseData } from "@/types/common";
import { pickDefined } from "@/lib/utils/pick-defined";
import type { QueryClient } from "@tanstack/react-query";
import { uploadFiles } from "./upload-files-service";

// Type for file upload data in inquiry message API
export type InquiryFileUploadData = {
  file_url: string;
  file_name: string;
  file_path: string;
  original_name?: string;
  file_type?: string;
};

const apiUrl = environments.restApiUrl;

export const patchInquiry = (id: string | number, content: string) => {
  return fetcher<ResponseData<InquiryData>>(`${apiUrl}/inquiry/${id}`, {
    method: "PATCH",
    headers: {
      accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ content }),
  });
};

export const getInquiryDetails = (id: string | number) => {
  return fetcher<ResponseData<InquiryData>>(`${apiUrl}/inquiry/${id}`, {
    method: "GET",
    headers: {
      accept: "application/json",
    },
  });
};

export const createInquiry = (data: InquiryRequest) => {
  return fetcher<ResponseData<InquiryData>>(`${apiUrl}/inquiry`, {
    method: "POST",
    headers: {
      accept: "application/json",
      "Content-Type": "application/json",
    },
    body: data,
  });
};

export const getInquiries = ({
  page = 1,
  page_size = 20,
  search,
  search_field,
  is_product,
  // some APIs accept `order_by` as the canonical param name
  order_by,
}: {
  page?: number;
  page_size?: number;
  search?: string | null;
  search_field?: string | null;
  // Swagger defines is_product as boolean; accept boolean or string here for compatibility
  is_product?: boolean | string | null;
  order_by?: string | null;
}) => {
  const queryObj = pickDefined({
    page,
    page_size,
    search,
    search_field,
    is_product,
    // prefer explicit order_by if provided, otherwise use sort_order for compatibility
    order_by: order_by,
  });

  const qs = new URLSearchParams(
    Object.entries(queryObj).reduce((acc, [k, v]) => {
      acc[k] = String(v);
      return acc;
    }, {} as Record<string, string>)
  ).toString();

  const url = qs ? `${apiUrl}/inquiry/list?${qs}` : `${apiUrl}/inquiry/list`;
  return fetcher<ResponseData<InquiryData[]>>(url, {
    method: "GET",
    headers: {
      accept: "application/json",
    },
  });
};

export const getInquiryMessagesQueryKey = (
  inquiryId: string | number | undefined,
  page = 1,
  page_size = 20
) => ["inquiry-messages", inquiryId, page, page_size] as const;

export const getInquiryMessages = (
  inquiryId: string | number | undefined,
  page = 1,
  page_size = 20
) => {
  if (!inquiryId) return Promise.resolve(null);

  const queryObj = pickDefined({
    page,
    page_size,
  });

  const qs = new URLSearchParams(
    Object.entries(queryObj).reduce((acc, [k, v]) => {
      acc[k] = String(v);
      return acc;
    }, {} as Record<string, string>)
  ).toString();

  const url = qs
    ? `${apiUrl}/inquiry/${inquiryId}/chat/messages?${qs}`
    : `${apiUrl}/inquiry/${inquiryId}/chat/messages`;

  return fetcher<ResponseData<unknown>>(url, {
    method: "GET",
    headers: {
      accept: "application/json",
    },
  });
};

export const sendInquiryMessage = async (
  inquiryId: number | string,
  message_content: string,
  file_upload_data?: InquiryFileUploadData[],
  queryClient?: QueryClient
) => {
  const requestBody: {
    request: {
      message_content: string;
      file_upload_data?: InquiryFileUploadData[];
    };
  } = {
    request: {
      message_content: message_content || "",
    },
  };

  // Only add file_upload_data if there are files
  if (file_upload_data && file_upload_data.length > 0) {
    requestBody.request.file_upload_data = file_upload_data;
  }

  const url = `${apiUrl}/inquiry/${inquiryId}/send-message`;
  const res = await fetcher<ResponseData<unknown>>(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: requestBody,
  });

  // If a QueryClient is provided, invalidate any inquiry-messages queries for this inquiryId
  if (queryClient) {
    try {
      await queryClient.invalidateQueries({
        queryKey: ["inquiry-messages", inquiryId],
        exact: false,
      });
    } catch (e) {
      console.error("Failed to invalidate inquiry messages queries", e);
    }
  }

  return res;
};

export const editInquiryMessage = async (
  inquiryId: number | string,
  messageId: number,
  messageContent: string,
  queryClient?: QueryClient
) => {
  const requestBody = {
    edit_message: {
      message_id: messageId,
      message_content: messageContent,
    },
  };

  const url = `${apiUrl}/inquiry/${inquiryId}/send-message`;
  const res = await fetcher<ResponseData<unknown>>(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: requestBody,
  });

  // Invalidation is now handled by hooks, but keep this for backward compatibility
  if (queryClient) {
    try {
      await queryClient.invalidateQueries({
        queryKey: ["inquiry-messages", inquiryId],
        exact: false,
      });
    } catch (e) {
      console.error("Failed to invalidate inquiry messages queries", e);
    }
  }

  return res;
};

export const uploadInquiryFiles = async (
  inquiryId: number | string,
  files: File[],
  message_content?: string,
  queryClient?: QueryClient
) => {
  // Allow text-only messages (when files.length === 0 but message_content exists)
  if (!inquiryId || (!files.length && !message_content?.trim())) {
    return Promise.resolve(null);
  }

  // Upload files first if any
  let file_upload_data: InquiryFileUploadData[] = [];

  if (files.length > 0) {
    try {
      const uploadResponse = await uploadFiles(files);
      // Transform upload response to InquiryFileUploadData format
      file_upload_data = uploadResponse?.map((fileInfo) => ({
        file_url: fileInfo.file_url,
        file_name: fileInfo.file_name,
        file_path: fileInfo.file_path,
        original_name: fileInfo.file_name,
        file_type: fileInfo.file_name.split(".").pop() || "", // Extract file extension
      }));
    } catch (error) {
      console.error("Failed to upload files:", error);
      throw error; // Re-throw to handle in component
    }
  }

  // Send message with file data
  return sendInquiryMessage(
    inquiryId,
    message_content || "",
    file_upload_data,
    queryClient
  );
};
